import type {
  CostCenter,
  CostCenterInconsistenciesResponse,
  CostCenterInconsistencyEntityType,
  CostCenterInconsistencyFinding,
  CostCenterInconsistencyFindingDTO,
  CostCenterInconsistencyStatus,
  CostCenterSummary,
} from '@src/models/interfaces/costCenter.interface';

const isEntityType = (value: unknown): value is CostCenterInconsistencyEntityType =>
  value === 'user' || value === 'journey';

const isStatus = (value: unknown): value is CostCenterInconsistencyStatus =>
  value === 'open' || value === 'resolved';

export class CostCenterMapper {
  static toSummary(cc: CostCenter): CostCenterSummary {
    return {
      id: cc.id,
      parentId: cc.parentId ?? null,
      code: cc.code,
      name: cc.name,
      active: Boolean(cc.active),
    };
  }

  static fromApi(raw: CostCenter): CostCenter {
    return {
      id: raw.id,
      organizationId: raw.organizationId,
      parentId: raw.parentId ?? null,
      code: raw.code,
      name: raw.name,
      description: raw.description,
      responsibleUserIds: Array.isArray(raw.responsibleUserIds) ? raw.responsibleUserIds : [],
      active: Boolean(raw.active),
      client: raw.client ?? null,
      contract: raw.contract ?? null,
      contracted: raw.contracted ?? null,
    };
  }

  static fromInconsistencyFindingDTO(
    id: string,
    dto: CostCenterInconsistencyFindingDTO,
  ): CostCenterInconsistencyFinding | null {
    if (!isEntityType(dto.entityType) || !dto.entityId || !isStatus(dto.status)) {
      return null;
    }
    return {
      id,
      organizationId: dto.organizationId ?? '',
      entityType: dto.entityType,
      entityId: String(dto.entityId),
      costCenterId: dto.costCenterId != null ? String(dto.costCenterId) : '',
      sourceCostCenterId:
        dto.sourceCostCenterId != null ? String(dto.sourceCostCenterId) : '',
      status: dto.status,
      warnings: Array.isArray(dto.warnings)
        ? dto.warnings.map(warning => ({
            code: warning.code,
            message: warning.message,
          }))
        : [],
    };
  }

  /**
   * Maps on-demand callable results into open findings for fallback / refresh.
   * Doc ids match GUA-333 `findingIdFor`: `user_{entityId}` / `journey_{entityId}`.
   * The live/scan merge still keys by entityType:entityId as a safety net.
   */
  static fromInconsistenciesScan(
    organizationId: string,
    response: CostCenterInconsistenciesResponse,
  ): CostCenterInconsistencyFinding[] {
    const users = (response.users ?? []).map(row => ({
      id: `user_${row.userId}`,
      organizationId,
      entityType: 'user' as const,
      entityId: row.userId,
      costCenterId: row.costCenterId,
      sourceCostCenterId: row.costCenterId,
      status: 'open' as const,
      warnings: row.warnings ?? [],
    }));
    const journeys = (response.journeys ?? []).map(row => ({
      id: `journey_${row.workJourneyConfigId}`,
      organizationId,
      entityType: 'journey' as const,
      entityId: row.workJourneyConfigId,
      costCenterId: row.costCenterId,
      sourceCostCenterId: row.costCenterId,
      status: 'open' as const,
      warnings: row.warnings ?? [],
    }));
    return [...users, ...journeys];
  }
}
